import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Web_Safe_Fonts = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🌐 Web Safe Fonts</h1>

      {/* Intro */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#166534", marginBottom: "0.5rem" }}>
          ✨ What are Web Safe Fonts?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Web safe fonts are the <strong>default fonts</strong> available on
          most devices and operating systems. Since not all computers have the
          same fonts installed, web designers use web safe fonts to ensure
          consistent typography across all browsers.
        </p>
      </div>

      {/* History */}
      <div className="tutorial-section">
        <h2 className="section-title">📜 A Little History</h2>
        <p>
          In the early days of the web, developers couldn’t rely on external
          font libraries. They had to choose from a limited set of fonts that
          were installed by default on Windows and macOS systems. This led to
          the concept of <strong>“web safe fonts”</strong>.
        </p>
      </div>

      {/* Categories */}
      <div className="tutorial-section">
        <h2 className="section-title">📂 Categories of Web Safe Fonts</h2>
        <ul className="tutorial-list">
          <li>
            <strong>Sans-serif</strong> → Modern, clean look (Arial, Verdana,
            Helvetica)
          </li>
          <li>
            <strong>Serif</strong> → Classic, formal look (Times New Roman,
            Georgia)
          </li>
          <li>
            <strong>Monospace</strong> → Code-like, equal spacing (Courier New,
            Lucida Console)
          </li>
        </ul>
      </div>

      {/* List of Fonts */}
      <div className="tutorial-section">
        <h2 className="section-title">📘 Popular Web Safe Fonts</h2>
        <table className="tutorial-table">
          <thead>
            <tr>
              <th>Font Name</th>
              <th>Category</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Arial</td>
              <td>Sans-serif</td>
              <td style={{ fontFamily: "Arial, sans-serif" }}>Sample Text</td>
            </tr>
            <tr>
              <td>Verdana</td>
              <td>Sans-serif</td>
              <td style={{ fontFamily: "Verdana, sans-serif" }}>Sample Text</td>
            </tr>
            <tr>
              <td>Times New Roman</td>
              <td>Serif</td>
              <td style={{ fontFamily: "Times New Roman, serif" }}>
                Sample Text
              </td>
            </tr>
            <tr>
              <td>Georgia</td>
              <td>Serif</td>
              <td style={{ fontFamily: "Georgia, serif" }}>Sample Text</td>
            </tr>
            <tr>
              <td>Courier New</td>
              <td>Monospace</td>
              <td style={{ fontFamily: "Courier New, monospace" }}>
                Sample Text
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Example */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example with Fallback</h2>
        <p>
          If the first font isn’t available, the browser uses the next one in
          the list. Always end with a <strong>generic family</strong> like{" "}
          <code>serif</code>, <code>sans-serif</code>, or <code>monospace</code>
          .
        </p>

        <CodeCopyBlock
          code={`body {
  font-family: "Arial", "Helvetica", sans-serif;
}

h1 {
  font-family: "Georgia", "Times New Roman", serif;
}

code {
  font-family: "Courier New", monospace;
}`}
        />
      </div>

      {/* Advantages */}
      <div className="tutorial-section">
        <h2 className="section-title">✅ Advantages of Web Safe Fonts</h2>
        <ul className="tutorial-list">
          <li>No need to load from the internet → faster performance</li>
          <li>Works on all major browsers and devices</li>
          <li>Lightweight and reliable for simple projects</li>
        </ul>
      </div>

      {/* Limitations */}
      <div className="tutorial-section">
        <h2 className="section-title">⚠️ Limitations</h2>
        <ul className="tutorial-list">
          <li>Very limited font choices (not great for unique branding)</li>
          <li>May look outdated compared to modern web fonts</li>
          <li>Design consistency can be challenging across platforms</li>
        </ul>
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Best Practices</h2>
        <ul className="tutorial-list">
          <li>
            Always include <strong>fallbacks</strong> in your font-family.
          </li>
          <li>
            Use web safe fonts for <strong>email templates</strong> and{" "}
            <strong>basic websites</strong>.
          </li>
          <li>
            Combine with{" "}
            <a
              href="https://fonts.google.com/"
              target="_blank"
              rel="noreferrer"
              className="learn-more"
            >
              Google Fonts
            </a>{" "}
            for modern design but keep a fallback ready.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know about <strong>Web Safe Fonts</strong>, the next
          topic is{" "}
          <a href="/css/google-fonts" className="learn-more">
            Google Fonts
          </a>{" "}
          – a huge library of free fonts you can easily add to your website.
        </p>
      </div>
    </div>
  );
};

export default Web_Safe_Fonts;
